import React, { useEffect, useRef, useState } from 'react';
import './banquet.css';
import AddAsNotInMenuOption from '../notInMenu/AddAsNotInMenuOption';
import { notInMenuOptionFor } from '../../utils/tsrNotInMenuLines';

/**
 * Banquet item cards:
 * Structured identically to Manual Entry item cards, but without Unit Price
 * or Total Price (buffet/banquet pricing applies to the package, not per dish).
 */
const BanquetItemRows = ({
  rows,
  platesOrdered,
  canAddItems,
  disabled,
  error,                 // duplicate-item message from the reducer
  getSuggestions,        // (query) => menu items, the page's own matcher
  getMenuItemName,       // (menuItem) => string, the page's own accessor
  onAddItem,
  onAddEmptyRow,
  onUpdateRowItem,
  onSetRowQty,
  onResetRowQty,
  onRemoveRow,
  onClearError,
  onMarkNotInMenu,       // (rowId, itemName) — the dropdown's "Add as Not in Menu"
}) => {
  const [activeRowId, setActiveRowId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [activeIdx, setActiveIdx] = useState(-1);
  const cardRefs = useRef({});
  const prevRowsLength = useRef(0);

  const focusInput = (rowId, field = 'item') => {
    setTimeout(() => {
      const el = cardRefs.current[`${field}-${rowId}`];
      if (el) {
        el.focus({ preventScroll: true });
        if (field === 'qty' && el.select) {
          el.select();
        }
      }
    }, 40);
  };

  useEffect(() => {
    if (rows.length > prevRowsLength.current) {
      const lastRow = rows[rows.length - 1];
      if (lastRow) {
        focusInput(lastRow.rowId, 'item');
      }
    }
    prevRowsLength.current = rows.length;
  }, [rows]);

  const handleSearch = (rowId, value) => {
    if (onClearError) onClearError();
    if (onUpdateRowItem) {
      onUpdateRowItem(rowId, null, value);
    }
    const list = value.trim() ? (getSuggestions(value) || []) : [];
    setSuggestions(list);
    setActiveRowId(rowId);
    setActiveIdx(-1);
  };

  const handleFocus = (rowId, currentName) => {
    if (currentName && currentName.trim()) {
      const list = getSuggestions(currentName) || [];
      if (list.length > 0 || (onMarkNotInMenu && notInMenuOptionFor(currentName))) {
        setSuggestions(list);
        setActiveRowId(rowId);
        setActiveIdx(-1);
      }
    }
  };

  const handlePick = (rowId, menuItem) => {
    if (!menuItem) return;
    const name = getMenuItemName ? getMenuItemName(menuItem) : (menuItem.name || menuItem.item_name);
    const id = menuItem.menu_item_id || menuItem.id;

    if (onUpdateRowItem) {
      onUpdateRowItem(rowId, id, name);
    } else if (onAddItem) {
      onAddItem({ menuItemId: id, itemName: name });
    }

    setSuggestions([]);
    setActiveRowId(null);
    setActiveIdx(-1);

    // Focus quantity input for this row immediately
    focusInput(rowId, 'qty');
  };

  const handleMark = (rowId, itemName) => {
    if (onMarkNotInMenu) onMarkNotInMenu(rowId, itemName);
    setSuggestions([]);
    setActiveRowId(null);
    setActiveIdx(-1);
    focusInput(rowId, 'qty');
  };

  const handleItemKeyDown = (e, row) => {
    // The "Add as Not in Menu" row sits after the dishes.
    const option = onMarkNotInMenu ? notInMenuOptionFor(row.itemName) : null;
    const count = suggestions.length + (option ? 1 : 0);
    if (e.key === 'ArrowDown' && count) {
      e.preventDefault();
      setActiveRowId(row.rowId);
      setActiveIdx((i) => Math.min(count - 1, i < 0 ? 0 : i + 1));
    } else if (e.key === 'ArrowUp' && count) {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i < 0 ? 0 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeRowId === row.rowId && option && activeIdx === suggestions.length) {
        handleMark(row.rowId, option);
      } else if (activeRowId === row.rowId && activeIdx >= 0 && suggestions[activeIdx]) {
        handlePick(row.rowId, suggestions[activeIdx]);
      } else if (activeRowId === row.rowId && suggestions.length > 0) {
        handlePick(row.rowId, suggestions[0]);
      } else {
        setActiveRowId(null);
        setSuggestions([]);
        focusInput(row.rowId, 'qty');
      }
    } else if (e.key === 'Escape') {
      setActiveRowId(null);
      setSuggestions([]);
      setActiveIdx(-1);
    } else if (e.key === 'Tab' && !e.shiftKey) {
      setActiveRowId(null);
      setSuggestions([]);
    }
  };

  const handleRemove = (rowId) => {
    if (rows.length > 1) {
      onRemoveRow(rowId);
    } else {
      // Clear the single card so it remains ready for fresh input
      if (onUpdateRowItem) {
        onUpdateRowItem(rowId, null, '');
      }
      if (onResetRowQty) {
        onResetRowQty(rowId);
      }
    }
  };

  const displayRows = rows.length > 0
    ? rows
    : [{ rowId: 1, menuItemId: null, itemName: '', quantity: platesOrdered ?? null, quantitySource: 'inherited' }];

  const areAllRowsFilled = displayRows.length > 0 && displayRows.every(
    (r) => Boolean((r.menuItemId || r.notInMenu) && r.itemName && r.itemName.trim() && r.quantity !== null && Number(r.quantity) > 0)
  );

  const handleAddMore = (e) => {
    e?.currentTarget?.blur();
    if (!areAllRowsFilled) return;
    if (onAddEmptyRow) {
      onAddEmptyRow();
    }
  };

  const handleQtyKeyDown = (e, row, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // If current row has quantity null/empty, default to platesOrdered if available
      if ((row.quantity === null || row.quantity === '' || isNaN(row.quantity)) && platesOrdered !== null && platesOrdered > 0) {
        if (onSetRowQty) {
          onSetRowQty(row.rowId, platesOrdered);
        }
      }

      const isCurrentRowFilled = Boolean(row.itemName && row.itemName.trim());

      if (isCurrentRowFilled) {
        if (index < displayRows.length - 1) {
          // Move focus to next existing row's item name
          const nextRow = displayRows[index + 1];
          if (nextRow) {
            focusInput(nextRow.rowId, 'item');
          }
        } else if (onAddEmptyRow && canAddItems !== false) {
          // Open next card
          onAddEmptyRow();
        }
      }
    }
  };

  return (
    <div className="tsr-manual-entry-wrap">
      <div
        className="tsr-manual-section-title"
        style={{
          textAlign: 'center',
          fontWeight: 700,
          fontSize: '1.1rem',
          margin: '12px 0 14px',
          color: '#111827',
        }}
      >
        Items
      </div>

      {displayRows.map((row, index) => {
        const isOverride = row.quantitySource === 'override';
        const nimOption = onMarkNotInMenu ? notInMenuOptionFor(row.itemName) : null;
        const isSuggestionsOpen = activeRowId === row.rowId && (suggestions.length > 0 || Boolean(nimOption));

        return (
          <div className="tsr-manual-item-card" key={row.rowId || `item-row-${index}`}>
            <button
              type="button"
              className="tsr-item-remove-top"
              onClick={() => handleRemove(row.rowId)}
              disabled={disabled}
              aria-label={`Remove ${row.itemName || 'item'}`}
              title="Remove"
            >
              ✕
            </button>

            <div className="bq-item-row-fields">
              <div className="bq-item-row-num" aria-label={`Item ${index + 1}`}>
                {index + 1}.
              </div>

              <div className="tsr-manual-form-group bq-item-name-group" style={{ position: 'relative' }}>
                <label className="tsr-manual-label" htmlFor={`bq-item-${row.rowId}`}>
                  Item Name
                </label>
                <input
                  id={`bq-item-${row.rowId}`}
                  type="text"
                  className="tsr-manual-input"
                  placeholder="Type to search menu items..."
                  value={row.itemName || ''}
                  disabled={disabled}
                  onChange={(e) => handleSearch(row.rowId, e.target.value)}
                  onFocus={() => handleFocus(row.rowId, row.itemName)}
                  onBlur={() => setTimeout(() => {
                    if (activeRowId === row.rowId) {
                      setActiveRowId(null);
                    }
                  }, 200)}
                  onKeyDown={(e) => handleItemKeyDown(e, row)}
                  aria-label="Item Name"
                  ref={(el) => { cardRefs.current[`item-${row.rowId}`] = el; }}
                />

                {isSuggestionsOpen && (
                  <div className="tsr-manual-suggestions">
                    {suggestions.map((menuItem, idx) => {
                      const id = menuItem.menu_item_id || menuItem.id;
                      const name = getMenuItemName ? getMenuItemName(menuItem) : (menuItem.name || menuItem.item_name);
                      return (
                        <div
                          key={id || idx}
                          className={`tsr-manual-suggestion${idx === activeIdx ? ' active' : ''}`}
                          onMouseEnter={() => setActiveIdx(idx)}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handlePick(row.rowId, menuItem)}
                        >
                          <span className="tsr-manual-suggestion-name">{name}</span>
                        </div>
                      );
                    })}
                    {nimOption && (
                      <AddAsNotInMenuOption
                        typedName={nimOption}
                        active={activeIdx === suggestions.length}
                        onHover={() => setActiveIdx(suggestions.length)}
                        onPick={() => handleMark(row.rowId, nimOption)}
                      />
                    )}
                  </div>
                )}
                {row.notInMenu && <span className="tsr-nim-badge">Not in Menu</span>}
              </div>

              <div className="tsr-manual-form-group bq-item-qty-group">
                <label className="tsr-manual-label" htmlFor={`bq-qty-${row.rowId}`}>
                  Quantity
                </label>
                <input
                  id={`bq-qty-${row.rowId}`}
                  type="number"
                  className="tsr-manual-input"
                  placeholder="Enter Quantity"
                  value={row.quantity ?? ''}
                  min="0.001"
                  step="1"
                  disabled={disabled}
                  ref={(el) => { cardRefs.current[`qty-${row.rowId}`] = el; }}
                  onChange={(e) => onSetRowQty(row.rowId, e.target.value === '' ? null : Number(e.target.value))}
                  onKeyDown={(e) => handleQtyKeyDown(e, row, index)}
                />

                {isOverride && platesOrdered !== null && (
                  <div className="bq-qty-hint is-override" style={{ marginTop: '4px' }}>
                    <span>override</span>
                    <button
                      type="button"
                      className="bq-reset-qty"
                      onClick={() => onResetRowQty(row.rowId)}
                      disabled={disabled}
                    >
                      ↺ reset to {platesOrdered}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {error && <div className="bq-field-error bq-field-error-block">{error}</div>}

      {/* Add More Items Button */}
      <button
        type="button"
        className="tsr-add-more-items-dashed"
        onClick={handleAddMore}
        disabled={disabled || !canAddItems || !areAllRowsFilled}
      >
        + Add More Items
      </button>
    </div>
  );
};

export default BanquetItemRows;
